import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sparkles, Users, MessageSquare, Dice6, MapPin, Zap, ChevronRight } from "lucide-react";
import SeoHead from "../components/SeoHead";

/**
 * SEO landing page for MytharionVTT.
 *
 * Public, indexable marketing page. The /vtt route (the actual app) remains
 * behind auth + redeem gate; this page explains the product and links to it.
 */
export default function MytharionVttLanding() {
  const { t } = useTranslation();

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "MytharionVTT",
    applicationCategory: "GameApplication",
    operatingSystem: "Web",
    description: t("landing.vtt.seoDesc"),
    url: "https://mytharion.app/mytharion-vtt",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "EUR",
      availability: "https://schema.org/InStock",
      description: "Free Beta access via invite code",
    },
    publisher: { "@type": "Organization", name: "Mytharion", url: "https://mytharion.app/" },
  };

  const Feature = ({ icon: Icon, title, desc }) => (
    <div className="rune-border p-5 bg-card/60 rounded-lg">
      <Icon className="w-6 h-6 text-gold mb-3" />
      <h3 className="font-heading text-base text-parchment mb-1">{title}</h3>
      <p className="text-sm text-muted-foreground">{desc}</p>
    </div>
  );

  return (
    <>
      <SeoHead
        title={t("landing.vtt.seoTitle")}
        description={t("landing.vtt.seoDesc")}
        path="/mytharion-vtt"
        jsonLd={jsonLd}
      />
      <div className="container py-16 space-y-16">
        <header className="max-w-3xl">
          <p className="text-xs font-heading uppercase tracking-widest text-arcane mb-3">
            {t("landing.eyebrow")} · {t("landing.vtt.eyebrowSub")}
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-gold mb-4">
            {t("landing.vtt.h1")}
          </h1>
          <p className="text-base text-parchment/90 mb-6 leading-relaxed">
            {t("landing.vtt.lead")} <strong>Dungeons &amp; Dragons</strong>{" "}
            {t("landing.vtt.leadTail")}
          </p>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/vtt"
              data-testid="vtt-landing-cta-open"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-gradient-to-b from-yellow-600 to-yellow-800 text-black font-heading gold-glow"
            >
              {t("landing.vtt.ctaOpen")} <ChevronRight className="w-4 h-4" />
            </Link>
            <Link
              to="/store"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-md rune-border text-gold hover:bg-yellow-900/20 font-heading"
            >
              {t("landing.vtt.ctaAllProducts")}
            </Link>
          </div>
        </header>

        <section aria-labelledby="vtt-features">
          <h2 id="vtt-features" className="font-display text-3xl text-gold mb-6">
            {t("landing.vtt.featuresTitle")}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Feature icon={MapPin} title={t("landing.vtt.f1Title")} desc={t("landing.vtt.f1Desc")} />
            <Feature icon={Users} title={t("landing.vtt.f2Title")} desc={t("landing.vtt.f2Desc")} />
            <Feature icon={Dice6} title={t("landing.vtt.f3Title")} desc={t("landing.vtt.f3Desc")} />
            <Feature icon={MessageSquare} title={t("landing.vtt.f4Title")} desc={t("landing.vtt.f4Desc")} />
            <Feature icon={Sparkles} title={t("landing.vtt.f5Title")} desc={t("landing.vtt.f5Desc")} />
            <Feature icon={Zap} title={t("landing.vtt.f6Title")} desc={t("landing.vtt.f6Desc")} />
          </div>
        </section>

        <section aria-labelledby="vtt-who">
          <h2 id="vtt-who" className="font-display text-3xl text-gold mb-6">
            {t("landing.vtt.whoTitle")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-6 rune-border bg-card/60 rounded-lg">
              <h3 className="font-heading text-lg text-parchment mb-2">
                {t("landing.vtt.gmTitle")}
              </h3>
              <p className="text-sm text-muted-foreground">{t("landing.vtt.gmDesc")}</p>
            </div>
            <div className="p-6 rune-border bg-card/60 rounded-lg">
              <h3 className="font-heading text-lg text-parchment mb-2">
                {t("landing.vtt.playersTitle")}
              </h3>
              <p className="text-sm text-muted-foreground">{t("landing.vtt.playersDesc")}</p>
            </div>
          </div>
        </section>

        <section aria-labelledby="vtt-ecosystem">
          <h2 id="vtt-ecosystem" className="font-display text-3xl text-gold mb-6">
            {t("landing.vtt.ecosystemTitle")}
          </h2>
          <p className="text-base text-parchment/90 mb-4">
            {t("landing.vtt.ecosystemLead")}
            <Link to="/cartomancer" className="text-gold hover:underline ml-1">
              Cartomancer
            </Link>{" "}
            {t("landing.vtt.ecosystemHelp")}
          </p>
        </section>
      </div>
    </>
  );
}
